import { useState } from "react";
import { CheckCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { z } from "zod";
import { SectionHeading } from "./NewsSection";
import LegalHub from "@/components/LegalHub";
import { submitLead } from "@/lib/submitLead";
import { toast } from "sonner";
import MarketingConsent from "@/components/MarketingConsent";
import { isValidPhoneNumber } from "react-phone-number-input";
import {
  WmtFormInput,
  WmtFormSubmitButton,
  WmtPhoneInput,
} from "@/components/ui/wmt-form-controls";

const contactSchema = z
  .object({
    name: z.string().trim().min(1, "Укажите ФИО").max(100, "ФИО слишком длинное"),
    email: z.string().trim().email("Укажите корректный email").max(255).optional().or(z.literal("")),
    phone: z
      .string()
      .max(30)
      .optional()
      .or(z.literal(""))
      .refine((v) => !v || isValidPhoneNumber(v), { message: "Введите корректный номер телефона" }),
    message: z.string().trim().min(1, "Опишите ваш запрос").max(2000, "Сообщение слишком длинное"),
  })
  .refine((d) => !!d.email || !!d.phone, {
    message: "Укажите email или телефон",
    path: ["email"],
  });

type ContactForm = z.infer<typeof contactSchema>;

const ContactSection = () => {
  const [form, setForm] = useState<ContactForm>({ name: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState<Partial<Record<keyof ContactForm, string>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [consent, setConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field: keyof ContactForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let hasError = false;
    const result = contactSchema.safeParse(form);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof ContactForm, string>> = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as keyof ContactForm;
        if (!fieldErrors[field]) fieldErrors[field] = err.message;
      });
      setErrors(fieldErrors);
      hasError = true;
    }
    if (!consent) {
      setConsentError(true);
      hasError = true;
    }
    if (hasError || !result.success) return;

    setSubmitting(true);
    try {
      await submitLead({
        form_type: "contact",
        name: result.data.name,
        email: result.data.email || null,
        phone: result.data.phone || null,
        message: result.data.message,
        marketing_consent: marketingConsent,
      });
    } catch {
      toast.error("Не удалось отправить заявку. Попробуйте позже.");
      return;
    } finally {
      setSubmitting(false);
    }

    setSubmitted(true);
  };

  return (
    <section id="contact" className="relative z-10 -mt-8 overflow-hidden rounded-t-[32px] bg-surface pb-24 pt-32 sm:-mt-14 sm:rounded-t-[56px] sm:pb-24 sm:pt-[152px]">
      <div className="absolute inset-0 grid-pattern opacity-20" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="flex w-full justify-start sm:justify-center">
              <SectionHeading underline={false} className="whitespace-normal text-left sm:whitespace-nowrap sm:text-center">
                <span className="inline">Обсудить </span>
                <span className="inline text-accent">первые шаги</span>
              </SectionHeading>
            </div>
            <p className="text-text-subtle max-w-2xl mt-6 mx-auto text-center text-lg">
              Обсудим, какой шаг подходит вашей компании&nbsp;— обучение, готовое решение или AI-трансформация
            </p>
          </motion.div>

          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-12 border border-accent/30 rounded-lg bg-card"
              >
                <CheckCircle size={48} className="text-accent mx-auto mb-4" />
                <h3 className="font-heading text-xl mb-2">Спасибо за заявку</h3>
                <p className="text-sm text-muted-foreground">Скоро с вами свяжется менеджер</p>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <WmtFormInput
                    type="text"
                    placeholder="ФИО"
                    value={form.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    error={errors.name}
                  />
                  <WmtFormInput
                    type="email"
                    placeholder="Email"
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    error={errors.email}
                  />
                </div>
                <WmtPhoneInput
                  value={form.phone || ""}
                  onChange={(value) => handleChange("phone", value)}
                  error={errors.phone}
                  helperText="Укажите email или телефон"
                />
                <div>
                  <textarea
                    placeholder="Опишите свой запрос, вашу компанию и должность"
                    rows={4}
                    value={form.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    className={`w-full px-4 py-3 rounded-lg bg-card border text-sm font-body text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent transition-[border-color,box-shadow,background-color] resize-none ${
                      errors.message ? "border-destructive" : "border-border"
                    }`}
                  />
                  {errors.message && <p className="text-xs text-destructive mt-1">{errors.message}</p>}
                </div>

                <div className="flex items-start gap-2 pt-1">
                  <input
                    id="contact-consent"
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => {
                      setConsent(e.target.checked);
                      if (e.target.checked) setConsentError(false);
                    }}
                    className="mt-0.5 h-3.5 w-3.5 rounded border-border accent-accent shrink-0"
                  />
                  <label htmlFor="contact-consent" className="leading-relaxed text-muted-foreground/70 text-sm text-left">
                    Я даю согласие на обработку персональных данных на условиях{" "}
                    <button
                      type="button"
                      onClick={() => setLegalOpen(true)}
                      className="underline underline-offset-2 hover:text-foreground transition-colors"
                    >
                      отдельного согласия
                    </button>.
                  </label>
                </div>
                {consentError && (
                  <p className="text-xs text-destructive -mt-2">Необходимо дать согласие на обработку данных</p>
                )}
                <MarketingConsent id="contact-marketing-consent" checked={marketingConsent} onChange={setMarketingConsent} />

                <div className="flex flex-col items-center gap-4 pt-2">
                  <WmtFormSubmitButton submitting={submitting}>
                    <span className="optical-center">Оставить заявку</span>
                  </WmtFormSubmitButton>
                </div>

                <LegalHub open={legalOpen} onOpenChange={setLegalOpen} defaultSection="confidentiality" />
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
